/**
 * One-way legacy Markdown → `.zwdoc` conversion (#248, used by #259 and
 * the history panel for pre-conversion commits).
 *
 * Handles every syntax the former Markdown editor wrote:
 * - own-line `zw-align` / `zw-indent` wrappers (and legacy `<div style>`
 *   alignment) → paragraph / heading attrs
 * - `zw-spacer` lines → `spacer` nodes
 * - `zw-c` range markers → `comment` marks; open-only markers → `comment_anchor`
 * - trailing `<!-- zw-comments -->` store → `header.comments`
 * - `==highlight==`, `<u>…</u>`, `~~strike~~` alongside standard GFM inline
 *
 * Unmodelled blocks (tables, HTML blocks) become `raw` nodes holding the
 * original Markdown. Frontmatter is not parsed here: callers pass the header
 * values (Rust already owns YAML frontmatter), and `stripLegacyFrontmatter`
 * removes the block textually where only prose matters.
 */

import type { Token, Tokens } from "marked";
import {
  ALIGN_CLOSE,
  ALIGN_CLOSE_LEGACY,
  ALIGN_OPEN,
  ALIGN_OPEN_LEGACY,
} from "./legacy/alignMarkers";
import { parseCommentStore, removeStoreBlock } from "./legacy/commentStore";
import { isIndentClose, parseIndentOpen } from "./legacy/indentMarkers";
import { parseSpacerLine, spacerHeightToCss } from "./legacy/spacerMarkers";
import { assertNever } from "../assertNever";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  nothing,
  type Maybe,
} from "../maybe";
import type { Result } from "../result";
import { zwDocFromFile, type ZwDoc, type ZwDocParseError } from "./format";
import { markdownLexer, type HighlightToken } from "./markdownLexer";
import type {
  ZwDocAlign,
  ZwDocBlock,
  ZwDocCommentThread,
  ZwDocEntityFields,
  ZwDocHeader,
  ZwDocInline,
  ZwDocListItem,
  ZwDocMark,
  ZwDocKind,
} from "./generated/zwDoc";
import { ZWDOC_MAX_INDENT } from "./schema";

export type LegacyMarkdownDocument = {
  /** File body with YAML frontmatter already removed. */
  markdown: string;
  synopsis: string;
  kind: Maybe<ZwDocKind>;
  fields: Maybe<ZwDocEntityFields>;
};

export function markdownToZwDoc(
  input: LegacyMarkdownDocument,
): Result<ZwDoc, ZwDocParseError> {
  const source = input.markdown.replace(/\r\n?/g, "\n");
  const header: ZwDocHeader = {
    zw: 1,
    synopsis: input.synopsis,
    kind: fromMaybe(input.kind),
    fields: fromMaybe(input.fields),
    comments: normalizeCommentStore(parseCommentStore(source)),
  };

  const comments = new CommentMarkers(removeStoreBlock(source));
  const converter = new BlockConverter(comments);
  const blocks: ZwDocBlock[] = [];
  for (const segment of segmentLines(comments.text)) {
    if (segment.kind === "spacer") {
      blocks.push({ type: "spacer", attrs: { height: segment.height } });
      continue;
    }
    const tokens = markdownLexer.lexer(segment.lines.join("\n"));
    blocks.push(...converter.blocks(tokens, segment.align, segment.indent));
  }

  const [first, ...rest] = blocks;
  return zwDocFromFile({
    header,
    blocks:
      typeof first === "undefined"
        ? [{ type: "paragraph", attrs: { align: "left", indent: 0 } }]
        : [first, ...rest],
  });
}

/**
 * Drop leading YAML frontmatter without parsing it. Mirrors the Rust readers:
 * at most two stacked blocks (the #72 double-frontmatter repair).
 */
export function stripLegacyFrontmatter(raw: string): string {
  let body = raw.replace(/\r\n?/g, "\n");
  for (let pass = 0; pass < 2; pass++) {
    if (body.startsWith("---\n") === false) break;
    const end = body.indexOf("\n---\n", 3);
    if (end === -1) break;
    body = body.slice(end + "\n---\n".length);
  }
  return body;
}

// --- Comment markers -----------------------------------------------------

const COMMENT_MARKER_RE = /<!--\s*(\/?)zw-c:([A-Za-z0-9_-]+)\s*-->/g;
const SENTINEL_OPEN = "\uE000";
const SENTINEL_CLOSE = "\uE001";
const SENTINEL_END = "\uE002";
const SENTINEL_RE = /([\uE000\uE001])(\d+)\uE002/g;
const SENTINEL_CHARS_RE = /[\uE000-\uE002]/g;

/** `comments` is the set of ranges open at that point in the run. */
type CommentOp =
  | { kind: "text"; text: string; comments: readonly string[] }
  | { kind: "anchor"; id: string; comments: readonly string[] };

/**
 * Replaces comment markers with private-use sentinels before lexing, so an
 * own-line or line-leading `<!-- zw-c -->` can't turn its paragraph into an
 * HTML block. Tracks which ranges are open while blocks are converted.
 */
class CommentMarkers {
  readonly text: string;
  private readonly ids: string[] = [];
  private readonly markers: string[] = [];
  private readonly closed = new Set<string>();
  private readonly active: string[] = [];

  constructor(source: string) {
    const cleaned = source.replace(SENTINEL_CHARS_RE, "");
    for (const match of cleaned.matchAll(COMMENT_MARKER_RE)) {
      const id = match[2];
      if (match[1] === "/" && typeof id === "string") this.closed.add(id);
    }
    this.text = cleaned.replace(
      COMMENT_MARKER_RE,
      (marker: string, slash: string, id: string) => {
        const index = this.markers.length;
        this.ids.push(id);
        this.markers.push(marker);
        const lead = slash === "/" ? SENTINEL_CLOSE : SENTINEL_OPEN;
        return `${lead}${index}${SENTINEL_END}`;
      },
    );
  }

  /** Comment ids whose range covers the current position. */
  activeIds(): readonly string[] {
    return this.active;
  }

  /** Split text into runs and comment events, updating open ranges. */
  ops(text: string): CommentOp[] {
    const ops: CommentOp[] = [];
    let last = 0;
    for (const match of text.matchAll(SENTINEL_RE)) {
      const index = match.index;
      const id = this.ids[Number(match[2])];
      if (index > last) {
        ops.push({
          kind: "text",
          text: text.slice(last, index),
          comments: [...this.active],
        });
      }
      last = index + match[0].length;
      if (typeof id === "undefined") continue;

      if (match[1] === SENTINEL_CLOSE) {
        const open = this.active.indexOf(id);
        if (open !== -1) this.active.splice(open, 1);
      } else if (this.closed.has(id)) {
        if (this.active.includes(id) === false) this.active.push(id);
      } else {
        ops.push({ kind: "anchor", id, comments: [...this.active] });
      }
    }
    if (last < text.length) {
      ops.push({
        kind: "text",
        text: text.slice(last),
        comments: [...this.active],
      });
    }
    return ops;
  }

  /** Text with sentinels removed (code blocks); still tracks open ranges. */
  strip(text: string): string {
    return this.ops(text)
      .map((op) => (op.kind === "text" ? op.text : ""))
      .join("");
  }

  /** Put original markers back (raw blocks keep source verbatim). */
  restore(text: string): string {
    this.ops(text);
    return text.replace(
      SENTINEL_RE,
      (_match: string, _lead: string, index: string) =>
        this.markers[Number(index)] ?? "",
    );
  }
}

function normalizeCommentStore(
  store: ReturnType<typeof parseCommentStore>,
): Record<string, ZwDocCommentThread> {
  const out: Record<string, ZwDocCommentThread> = {};
  for (const [id, thread] of Object.entries(store)) {
    if (typeof thread !== "object" || thread === null) continue;
    const messages = Array.isArray(thread.messages) ? thread.messages : [];
    out[id] = {
      createdAt: typeof thread.createdAt === "string" ? thread.createdAt : "",
      resolved: thread.resolved === true,
      messages: messages
        .filter(
          (message) =>
            typeof message === "object" &&
            message !== null &&
            typeof message.id === "string" &&
            typeof message.body === "string",
        )
        .map((message) => ({
          id: message.id,
          body: message.body,
          createdAt:
            typeof message.createdAt === "string" ? message.createdAt : "",
          ...(typeof message.authorId === "string"
            ? { authorId: message.authorId }
            : {}),
          ...(typeof message.authorName === "string"
            ? { authorName: message.authorName }
            : {}),
        })),
    };
  }
  return out;
}

// --- Structural wrapper lines --------------------------------------------

type Segment =
  | { kind: "markdown"; lines: string[]; align: ZwDocAlign; indent: number }
  | { kind: "spacer"; height: string };

type Wrapper =
  | { kind: "align"; align: ZwDocAlign; legacy: boolean }
  | { kind: "indent"; level: number };

const FENCE_RE = /^ {0,3}(`{3,}|~{3,})/;

function alignOpen(line: string): Maybe<Wrapper> {
  const trimmed = line.trim();
  const modern = ALIGN_OPEN.exec(trimmed);
  const legacy = modern === null ? ALIGN_OPEN_LEGACY.exec(trimmed) : null;
  const value = (modern ?? legacy)?.[1];
  if (typeof value === "undefined") return nothing;
  return just({
    kind: "align",
    align: value.toLowerCase() as ZwDocAlign,
    legacy: legacy !== null,
  });
}

function popWrapper(stack: Wrapper[], match: (w: Wrapper) => boolean): boolean {
  for (let i = stack.length - 1; i >= 0; i--) {
    const wrapper = stack[i];
    if (typeof wrapper !== "undefined" && match(wrapper)) {
      stack.splice(i, 1);
      return true;
    }
  }
  return false;
}

function currentAlign(stack: readonly Wrapper[]): ZwDocAlign {
  for (let i = stack.length - 1; i >= 0; i--) {
    const wrapper = stack[i];
    if (wrapper?.kind === "align") return wrapper.align;
  }
  return "left";
}

function currentIndent(stack: readonly Wrapper[]): number {
  for (let i = stack.length - 1; i >= 0; i--) {
    const wrapper = stack[i];
    if (wrapper?.kind === "indent")
      return Math.min(wrapper.level, ZWDOC_MAX_INDENT);
  }
  return 0;
}

/** Split the document at wrapper / spacer lines (outside fenced code). */
function segmentLines(text: string): Segment[] {
  const segments: Segment[] = [];
  const stack: Wrapper[] = [];
  let lines: string[] = [];
  let fence: Maybe<string> = nothing;

  const flush = (): void => {
    if (lines.some((line) => line.trim() !== "")) {
      segments.push({
        kind: "markdown",
        lines,
        align: currentAlign(stack),
        indent: currentIndent(stack),
      });
    }
    lines = [];
  };

  for (const line of text.split("\n")) {
    const fenceMatch = FENCE_RE.exec(line)?.[1];
    if (typeof fenceMatch === "string") {
      const marker = fenceMatch.charAt(0);
      if (isNothing(fence)) fence = just(marker);
      else if (fence.value === marker) fence = nothing;
      lines.push(line);
      continue;
    }
    if (isJust(fence)) {
      lines.push(line);
      continue;
    }

    const spacer = parseSpacerLine(line);
    if (spacer !== null) {
      flush();
      segments.push({ kind: "spacer", height: spacerHeightToCss(spacer) });
      continue;
    }

    const align = alignOpen(line);
    if (isJust(align)) {
      flush();
      stack.push(align.value);
      continue;
    }
    const trimmed = line.trim();
    if (ALIGN_CLOSE.test(trimmed)) {
      flush();
      popWrapper(stack, (w) => w.kind === "align" && w.legacy === false);
      continue;
    }
    // A bare `</div>` only closes legacy alignment when one is open.
    if (
      ALIGN_CLOSE_LEGACY.test(trimmed) &&
      stack.some((w) => w.kind === "align" && w.legacy)
    ) {
      flush();
      popWrapper(stack, (w) => w.kind === "align" && w.legacy);
      continue;
    }

    const indent = parseIndentOpen(line);
    if (indent !== null) {
      flush();
      stack.push({ kind: "indent", level: indent });
      continue;
    }
    if (isIndentClose(line)) {
      flush();
      popWrapper(stack, (w) => w.kind === "indent");
      continue;
    }

    lines.push(line);
  }
  flush();
  return segments;
}

// --- Tokens → nodes ------------------------------------------------------

const ENTITY_RE = /&(#x[0-9a-f]+|#[0-9]+|amp|lt|gt|quot|apos|nbsp);/gi;
const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: "\u00a0",
};

function decodeEntities(text: string): string {
  return text.replace(ENTITY_RE, (match: string, name: string) => {
    const lower = name.toLowerCase();
    if (lower.startsWith("#x"))
      return safeCodePoint(Number.parseInt(lower.slice(2), 16), match);
    if (lower.startsWith("#"))
      return safeCodePoint(Number.parseInt(lower.slice(1), 10), match);
    return NAMED_ENTITIES[lower] ?? match;
  });
}

function safeCodePoint(code: number, fallback: string): string {
  if (Number.isFinite(code) === false || code <= 0 || code > 0x10ffff)
    return fallback;
  return String.fromCodePoint(code);
}

type FormatMark = Exclude<ZwDocMark, { type: "comment" }>;

/** Per-textblock state for `<u>` / `</u>`, which arrive as sibling html tokens. */
type InlineState = { underline: number };

class BlockConverter {
  constructor(private readonly comments: CommentMarkers) {}

  blocks(
    tokens: readonly Token[],
    align: ZwDocAlign,
    indent: number,
  ): ZwDocBlock[] {
    const out: ZwDocBlock[] = [];
    for (const token of tokens) out.push(...this.block(token, align, indent));
    return out;
  }

  private block(token: Token, align: ZwDocAlign, indent: number): ZwDocBlock[] {
    switch (token.type) {
      // Task-list state is carried by `listItem` as a `[x] ` text prefix.
      case "space":
      case "checkbox":
        return [];
      case "paragraph":
      case "text": {
        const textblock = token as Tokens.Paragraph | Tokens.Text;
        const nodes = Array.isArray(textblock.tokens)
          ? this.inline(textblock.tokens, [], { underline: 0 })
          : this.text(decodeEntities(textblock.text), [], { underline: 0 });
        return [
          { type: "paragraph", attrs: { align, indent }, ...contentOf(nodes) },
        ];
      }
      case "heading": {
        const heading = token as Tokens.Heading;
        return [
          {
            type: "heading",
            attrs: {
              level: Math.min(Math.max(heading.depth, 1), 6),
              align,
              indent,
            },
            ...contentOf(this.inline(heading.tokens, [], { underline: 0 })),
          },
        ];
      }
      case "blockquote": {
        const quote = token as Tokens.Blockquote;
        return [
          {
            type: "blockquote",
            content: nonEmpty(this.blocks(quote.tokens, align, indent)),
          },
        ];
      }
      case "list": {
        const list = token as Tokens.List;
        const items = nonEmptyItems(
          list.items.map((item) => this.listItem(item, align, indent)),
        );
        if (list.ordered) {
          const start = typeof list.start === "number" ? list.start : 1;
          return [{ type: "ordered_list", attrs: { start }, content: items }];
        }
        return [{ type: "bullet_list", content: items }];
      }
      case "code": {
        const code = token as Tokens.Code;
        const text = this.comments.strip(code.text);
        return [
          {
            type: "code_block",
            attrs: { language: code.lang ?? "" },
            ...(text === "" ? {} : { content: [{ type: "text", text }] }),
          },
        ];
      }
      case "hr":
        return [{ type: "horizontal_rule" }];
      default:
        return [this.raw(token.raw)];
    }
  }

  private listItem(
    item: Tokens.ListItem,
    align: ZwDocAlign,
    indent: number,
  ): ZwDocListItem {
    const blocks = this.blocks(item.tokens, align, indent);
    if (item.task) {
      const box: ZwDocInline = {
        type: "text",
        text: item.checked === true ? "[x] " : "[ ] ",
      };
      const first = blocks[0];
      if (first?.type === "paragraph") {
        first.content = [box, ...(first.content ?? [])];
      } else {
        blocks.unshift({
          type: "paragraph",
          attrs: { align, indent },
          content: [box],
        });
      }
    }
    return { type: "list_item", content: nonEmpty(blocks) };
  }

  private raw(markdown: string): ZwDocBlock {
    return {
      type: "raw",
      attrs: { markdown: this.comments.restore(markdown).replace(/\n+$/, "") },
    };
  }

  private inline(
    tokens: readonly Token[],
    marks: readonly FormatMark[],
    state: InlineState,
  ): ZwDocInline[] {
    const out: ZwDocInline[] = [];
    for (const token of tokens)
      out.push(...this.inlineToken(token, marks, state));
    return out;
  }

  private inlineToken(
    token: Token,
    marks: readonly FormatMark[],
    state: InlineState,
  ): ZwDocInline[] {
    const withMark = (mark: FormatMark): FormatMark[] =>
      marks.some((m) => m.type === mark.type) ? [...marks] : [...marks, mark];

    switch (token.type) {
      case "text": {
        const text = token as Tokens.Text;
        if (Array.isArray(text.tokens))
          return this.inline(text.tokens, marks, state);
        return this.text(decodeEntities(text.text), marks, state);
      }
      case "escape":
        return this.text((token as Tokens.Escape).text, marks, state);
      case "strong":
        return this.inline(
          (token as Tokens.Strong).tokens,
          withMark({ type: "bold" }),
          state,
        );
      case "em":
        return this.inline(
          (token as Tokens.Em).tokens,
          withMark({ type: "italic" }),
          state,
        );
      case "del":
        return this.inline(
          (token as Tokens.Del).tokens,
          withMark({ type: "strike" }),
          state,
        );
      case "highlight":
        return this.inline(
          (token as unknown as HighlightToken).tokens,
          withMark({ type: "highlight" }),
          state,
        );
      case "codespan":
        return this.text(
          (token as Tokens.Codespan).text,
          withMark({ type: "code" }),
          state,
        );
      case "link": {
        const link = token as Tokens.Link;
        return this.inline(
          link.tokens,
          withMark({
            type: "link",
            attrs: { href: link.href, title: link.title ?? "" },
          }),
          state,
        );
      }
      case "image": {
        const image = token as Tokens.Image;
        return [
          this.withMarks(
            {
              type: "image",
              attrs: {
                src: image.href,
                alt: this.comments.strip(image.text),
                title: image.title ?? "",
              },
            },
            marks,
            state,
          ),
        ];
      }
      case "br":
        return [this.withMarks({ type: "hard_break" }, marks, state)];
      case "html": {
        const tag = token.raw.trim().toLowerCase();
        if (tag === "<u>") {
          state.underline += 1;
          return [];
        }
        if (tag === "</u>") {
          state.underline = Math.max(0, state.underline - 1);
          return [];
        }
        if (/^<br\s*\/?>$/.test(tag)) {
          return [this.withMarks({ type: "hard_break" }, marks, state)];
        }
        return this.text(token.raw, marks, state);
      }
      default:
        return this.text(token.raw, marks, state);
    }
  }

  /** Text run → text / hard_break / comment_anchor nodes. */
  private text(
    text: string,
    marks: readonly FormatMark[],
    state: InlineState,
  ): ZwDocInline[] {
    const out: ZwDocInline[] = [];
    for (const op of this.comments.ops(text)) {
      switch (op.kind) {
        case "anchor":
          out.push(
            this.withMarks(
              { type: "comment_anchor", attrs: { id: op.id } },
              marks,
              state,
              op.comments,
            ),
          );
          break;
        case "text": {
          const parts = op.text.split("\n");
          parts.forEach((part, index) => {
            if (index > 0) {
              out.push(
                this.withMarks(
                  { type: "hard_break" },
                  marks,
                  state,
                  op.comments,
                ),
              );
            }
            if (part !== "") {
              out.push(
                this.withMarks(
                  { type: "text", text: part },
                  marks,
                  state,
                  op.comments,
                ),
              );
            }
          });
          break;
        }
        default:
          assertNever(op);
      }
    }
    return out;
  }

  private withMarks<T extends ZwDocInline>(
    node: T,
    marks: readonly FormatMark[],
    state: InlineState,
    comments: readonly string[] = this.comments.activeIds(),
  ): T {
    const all: ZwDocMark[] = [...marks];
    if (
      state.underline > 0 &&
      marks.some((m) => m.type === "underline") === false
    ) {
      all.push({ type: "underline" });
    }
    for (const id of comments) {
      all.push({ type: "comment", attrs: { id } });
    }
    return all.length === 0 ? node : { ...node, marks: all };
  }
}

function contentOf(nodes: ZwDocInline[]): { content?: ZwDocInline[] } {
  return nodes.length === 0 ? {} : { content: nodes };
}

const EMPTY_PARAGRAPH: ZwDocBlock = {
  type: "paragraph",
  attrs: { align: "left", indent: 0 },
};

function nonEmpty(blocks: ZwDocBlock[]): [ZwDocBlock, ...ZwDocBlock[]] {
  const [first, ...rest] = blocks;
  return typeof first === "undefined" ? [EMPTY_PARAGRAPH] : [first, ...rest];
}

function nonEmptyItems(
  items: ZwDocListItem[],
): [ZwDocListItem, ...ZwDocListItem[]] {
  const [first, ...rest] = items;
  return typeof first === "undefined"
    ? [{ type: "list_item", content: [EMPTY_PARAGRAPH] }]
    : [first, ...rest];
}
